import { Flex, Select } from '@radix-ui/themes'
import { useTranslation } from 'react-i18next'
import { LuChartArea, LuChartColumnBig } from 'react-icons/lu'

const CONSUMPTION_CHART_TYPES = ['area', 'bar'] as const
export type ConsumptionChartType = (typeof CONSUMPTION_CHART_TYPES)[number]

const COMPARAISON_CHART_TYPES = ['area', 'bar', 'pie'] as const
export type ComparaisonChartType = (typeof COMPARAISON_CHART_TYPES)[number]

const ChartTypeIcons: Record<ComparaisonChartType | ConsumptionChartType, React.ReactNode> = {
  area: <LuChartArea size={'18'} />,
  bar: <LuChartColumnBig size={'18'} />,
  pie: <LuChartArea size={'18'} />
}

type Props<T extends ConsumptionChartType | ComparaisonChartType> = {
  types: T[]
  defaultType: T
  onTypeChange: (type: T) => void
  selectRootProps?: Omit<Select.RootProps, 'onValueChange' | 'value' | 'defaultValue'>
  selectContentProps?: Omit<Select.ContentProps, 'children'>
}

const ChartTypePicker = <T extends ConsumptionChartType | ComparaisonChartType>({
  types,
  defaultType,
  onTypeChange,
  ...selectProps
}: Props<T>) => {
  const { t } = useTranslation('common')
  const handleChange = (value: string) => {
    onTypeChange(value as T)
  }

  return (
    <Select.Root
      size={'3'}
      defaultValue={defaultType}
      onValueChange={handleChange}
      {...selectProps.selectRootProps}
    >
      <Select.Trigger />
      <Select.Content color="gray" variant="soft" position="popper" {...selectProps.selectContentProps}>
        {types.map((type) => (
          <Select.Item key={type} value={type}>
            <Flex gap="2" align={'center'}>
              {ChartTypeIcons[type]}
              {t(`chartType.${type}`)}
            </Flex>
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  )
}

export const ConsumptionChartTypePicker = (props: Omit<Props<ConsumptionChartType>, 'types'>) => {
  return (
    <ChartTypePicker
      {...props}
      // @ts-ignore this hardcast is safe because we know the types
      types={CONSUMPTION_CHART_TYPES}
    />
  )
}

export const ComparaisonChartTypePicker = (props: Omit<Props<ConsumptionChartType>, 'types'>) => {
  return (
    <ChartTypePicker
      {...props}
      // @ts-ignore this hardcast is safe because we know the types
      types={COMPARAISON_CHART_TYPES}
    />
  )
}
